import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { useModal } from '../../../../contexts/ModalContext';
import { FeatureFlags } from '../../../../flags';
import {
  MediaConfiguration,
  MediaLimits,
  MediaType,
  ValidationResult,
} from '../../../../system/media';
import ValidationResultsContent from '../../../common/ValidationResultsContent';
import { OfficeExclusiveType } from '../../control/OfficeExclusiveDocument';

const DEFAULT_CONFIGURATION: MediaConfiguration = {
  limits: {
    photos: { count: 50, size: '20mb' },
    tours: { count: 20, size: '20mb' },
    docs: { count: 20, size: '20mb' },
  },
};

interface ValidateUploadSetOptions {
  files: File[];
  type: MediaType;
  currentCount: number;
  docType?: string | null;
}

export function useMediaValidation() {
  const { hasFeature, getFeatureValue } = useFeatureFlags();
  const { showModal } = useModal();

  const getConfiguration = (): MediaConfiguration => {
    if (hasFeature(FeatureFlags.NGW_MEDIA)) {
      return getFeatureValue(FeatureFlags.NGW_MEDIA) || DEFAULT_CONFIGURATION;
    }
    return DEFAULT_CONFIGURATION;
  };

  const parseFileSize = (sizeString: string): number => {
    const match = sizeString.match(/^(\d+)(mb|kb)$/i);
    if (!match) throw new Error('Invalid size format');

    const [, size, unit] = match;
    return unit.toLowerCase() === 'mb'
      ? parseInt(size) * 1024 * 1024
      : parseInt(size) * 1024;
  };

  const validateUploadSet = async ({
    files,
    type,
    currentCount,
    docType = null,
  }: ValidateUploadSetOptions): Promise<File[]> => {
    const config = getConfiguration().limits[type];
    const maxBytes = parseFileSize(config.size);

    const validationResult: ValidationResult = {
      validFiles: [],
      countFailures: [],
      sizeFailures: [],
      nameFailures: [],
    };

    // First pass: Check file names for docs
    const nameValidFiles = files.filter((file) => {
      if (type === 'docs') {
        const nameWithoutExt =
          file.name.substring(0, file.name.lastIndexOf('.')) || file.name;

        // No file name length validation for Office exclusive files
        if (docType === OfficeExclusiveType.OFFICE_EXCLUSIVE) {
          return true;
        }

        if (nameWithoutExt.length > 50) {
          validationResult.nameFailures.push({
            file,
            reason: 'name',
            limit: '50 characters',
          });
          return false;
        }
      }
      return true;
    });

    // First pass: Check file sizes
    const sizeValidFiles = nameValidFiles.filter((file) => {
      if (file.size > maxBytes) {
        validationResult.sizeFailures.push({
          file,
          reason: 'size',
          limit: config.size,
        });
        return false;
      }
      return true;
    });

    // Second pass: Check counts only for size-valid files
    const remainingSlots = Math.max(0, config.count - currentCount);
    sizeValidFiles.forEach((file, index) => {
      if (index >= remainingSlots) {
        validationResult.countFailures.push({
          file,
          reason: 'count',
          limit: `${config.count} total files`,
        });
        return;
      }
      validationResult.validFiles.push(file);
    });

    if (
      validationResult.countFailures.length ||
      validationResult.sizeFailures.length ||
      validationResult.nameFailures.length
    ) {
      const shouldProceed = await showValidationModal(
        validationResult,
        type,
        currentCount,
        config,
      );
      if (!shouldProceed) {
        return [];
      }
    }

    return validationResult.validFiles;
  };

  const showValidationModal = (
    result: ValidationResult,
    type: MediaType,
    currentCount: number,
    config: MediaLimits,
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      showModal({
        title: 'File Upload Limit Reached',
        content: (
          <ValidationResultsContent
            result={result}
            type={type}
            currentCount={currentCount}
            config={config}
          />
        ),
        confirmText: result.validFiles.length > 0 ? 'Proceed' : undefined,
        cancelText: 'Try Again',
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
        width: 'md',
      });
    });
  };

  return { validateUploadSet };
}
